import {inject, Injectable} from '@angular/core';
import {Observable, of, throwError} from 'rxjs';
import {catchError, finalize, map, shareReplay, tap} from 'rxjs/operators';

import {AuthApiService} from '@app/features/auth/services/auth-api.service';
import {TokenService} from '@app/core/services/base/token.service';
import {AppNotifications} from '@shared/enums/app-notifications.enum';
import {ToastifyService} from '@shared/services/toastify.service';

@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService {
  private refreshInFlight$: Observable<string> | null = null;
  private readonly authApiService = inject(AuthApiService);

  constructor(
    private readonly tokenService: TokenService,
    private readonly toastifyService: ToastifyService,
  ) {}

  hasRefreshToken(): boolean {
    return !!this.tokenService.getRefreshToken();
  }

  ensureAccessToken(): Observable<boolean> {
    if (this.tokenService.hasAccessToken()) {
      return of(true);
    }
    if (!this.tokenService.hasRefreshToken()) {
      return of(false);
    }
    return this.refresh().pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  refresh(): Observable<string> {
    const refreshToken = this.tokenService.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('NO_REFRESH_TOKEN'));
    }
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.authApiService.refreshAccessToken(JSON.stringify(refreshToken)).pipe(
        tap((response) => {
          this.tokenService.setToken(response.result, this.tokenService.getRefreshToken());
        }),
        map((response) => response.result),
        catchError((error) => {
          this.refreshInFlight$ = null;
          this.tokenService.logOut();
          this.toastifyService.error(AppNotifications.LoginAgain);
          return throwError(() => error);
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay(1),
      );
    }
    return this.refreshInFlight$;
  }
}
